import React, { useState, useEffect } from "react";
import EmailLoginForm from "./EmailLoginForm";
import { FaCheckCircle } from "react-icons/fa";
import HostingerMailView from "./hostinger/hostingerEmailView";
import api from "../../../services/api";
import styles from "./EmailSelect.module.css";
import GmailMailView from "./gmail/gmailMailView";

const providers = ["hostinger", "gmail"] as const;
export type Provider = (typeof providers)[number];

const providerLogos: Record<Provider, string> = {
  hostinger: "https://cdn.worldvectorlogo.com/logos/hostinger.svg",
  gmail: "https://ssl.gstatic.com/ui/v1/icons/mail/rfr/gmail.ico",
};

const EmailSelect: React.FC = () => {
  const [selectedProvider, setSelectedProvider] =
    useState<Provider>("hostinger");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginTrigger, setLoginTrigger] = useState(0);
  const [skipNextAuthCheck, setSkipNextAuthCheck] = useState(false);
  const [oauth, setOauth] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isLogin, setIsLogin] = useState<any | null>(null);
  const [showGmailMessage, setShowGmailMessage] = useState(false);

  const signature = localStorage.getItem("signature");
  const token = localStorage.getItem("token");

  const checkAuthStatus = async (provider?: Provider) => {
    setIsCheckingAuth(true);
    try {
      if (!signature) {
        setIsAuthenticated(false);
        return;
      }
      let response = await api.post(
        "mailService/getLoginStatusForProviderEmail",
        {
          signature,
          provider: providers[0],
        }
      );
      if (response?.data?.data?.is_login) {
        setIsAuthenticated(response?.data.data.is_login);
        setOauth(response?.data.data?.oauth || null);
        setEmail(response?.data.data?.email || null);
        setIsLogin(response?.data.data?.is_login || null);
        if (response?.data.data?.provider) {
          setSelectedProvider(response?.data.data?.provider as Provider);
        }
      } else {
        response = await api.post(
          "mailService/getLoginStatusForProviderEmail",
          { signature, provider: providers[1] }
        );
        if (response?.data?.data?.is_login) {
          setIsAuthenticated(response?.data.data.is_login);
          setOauth(response?.data.data?.oauth || null);
          setEmail(response?.data.data?.email || null);
          setIsLogin(response?.data.data?.is_login || null);
          if (response?.data.data?.provider) {
            setSelectedProvider(response?.data.data?.provider as Provider);
          }
        }
      }
    } catch (error) {
      console.error("Error checking auth status:", error);
      setIsAuthenticated(false);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  useEffect(() => {
    if (skipNextAuthCheck) {
      setSkipNextAuthCheck(false);
      return;
    }
    checkAuthStatus();
  }, [signature, selectedProvider, loginTrigger]);

  const checkAuthStatusAndGetStatus = async (provider: Provider) => {
    try {
      if (!signature) {
        setIsAuthenticated(false);
        return null;
      }

      const response = await api.post(
        "mailService/getLoginStatusForProviderEmail",
        { signature, provider }
      );

      setIsAuthenticated(response.data.data.is_login);
      setOauth(response.data.data?.oauth || null);
      setEmail(response.data.data?.email || null);

      return {
        status: response.data.data?.status,
        provider: response.data.data?.provider,
        is_login: response.data.data.is_login,
      };
    } catch (error) {
      console.error("Error checking auth status:", error);
      setIsAuthenticated(false);
      return null;
    }
  };

  const handleSelect = async (provider: Provider) => {
    setSelectedProvider(provider);
    if (provider === "gmail") {
      setShowGmailMessage(true);
      try {
        if (!signature || !token) {
          setIsAuthenticated(false);
          return;
        }

        const authResponse = await checkAuthStatusAndGetStatus("gmail");

        if (authResponse?.is_login === true) {
          setSelectedProvider("gmail");
          setShowGmailMessage(false);
          return;
        }

        const encodedSignature = encodeURIComponent(signature);
        const encodedToken = encodeURIComponent(token);
        const redirectUrl = `${process.env.REACT_APP_API_URL}mailService/getGoogleOAuthRedirectController?signature=${encodedSignature}&provider=gmail&token=${encodedToken}`;

        const oauthWindow = window.open(
          redirectUrl,
          "_blank",
          "width=500,height=600,scrollbars=yes"
        );

        if (!oauthWindow) {
          throw new Error("Popup blocked or failed to open.");
        }

        const popupCheckInterval = setInterval(() => {
          if (oauthWindow.closed) {
            clearInterval(popupCheckInterval);
            if (!isAuthenticated) {
              setShowGmailMessage(false);
              setSelectedProvider("hostinger");
            }
          }
        }, 500);

        const messageListener = async (event: MessageEvent) => {
          if (event.origin !== `${process.env.REACT_APP_GMAIL_BACKEND_ORIGIN}`)
            return;

          if (event.data === "oauth-success") {
            clearInterval(popupCheckInterval);
            setSelectedProvider("gmail");
            setShowGmailMessage(false);
            await checkAuthStatus("gmail");
          } else {
            console.warn("OAuth failed or returned unknown status.");
          }

          window.removeEventListener("message", messageListener);
        };

        window.addEventListener("message", messageListener);

        return () => {
          clearInterval(popupCheckInterval);
          window.removeEventListener("message", messageListener);
        };
      } catch (error) {
        console.error("OAuth Redirect Error:", error);
        setShowGmailMessage(false);
        setSelectedProvider("hostinger");
      }
      return;
    }

    // For hostinger
    setSelectedProvider(provider);
    setShowGmailMessage(false);
  };

  const handleLoginSuccess = (email: string) => {
    setLoginTrigger((prev) => prev + 1);
    checkAuthStatus();
  };

  if (isCheckingAuth) {
    return (
      <div className={styles.loadingScreen}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {!isAuthenticated && (
          <div>
            {/* Header Card */}
            <div className={`${styles.headerCard} p-3 mb-3`}>
              <div className={`${styles.providerGroup}`}>
                {providers.map((provider) => (
                  <div
                    key={provider}
                    className={`${styles.providerOption} ${
                      selectedProvider === provider ? styles.active : ""
                    }`}
                    onClick={() => handleSelect(provider)}
                  >
                    <img
                      src={providerLogos[provider]}
                      alt={provider}
                      className={styles.providerIcon}
                    />
                    <span className={styles.providerName}>{provider}</span>
                    {selectedProvider === provider && (
                      <FaCheckCircle className={styles.checkIcon} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {showGmailMessage && (
          <div className={`alert ${styles.alertinfo} mt-3`}>
            You've selected Gmail. Please log in with your Google account to
            continue.
          </div>
        )}

        {/* Conditional rendering */}
        {isAuthenticated && selectedProvider === "gmail" && isLogin == true ? (
          <GmailMailView
            provider={selectedProvider}
            oauth={oauth}
            email={email}
          />
        ) : isAuthenticated && selectedProvider === "hostinger" ? (
          <HostingerMailView provider={selectedProvider} />
        ) : selectedProvider === "hostinger" && !showGmailMessage ? (
          <EmailLoginForm
            key={`login-form-${loginTrigger}`}
            provider={selectedProvider}
            onLoginSuccess={handleLoginSuccess}
          />
        ) : null}
      </div>
    </div>
  );
};

export default EmailSelect;
